import log from "loglevel";
import styles from "./DatePicker.module.css";
import React, { useState, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { getDaysInMonth } from "date-fns";
import { Calendar } from "react-ikonate";
import classNames from "classnames";
import { Modal } from "shared/Modal";
import TextInput from "shared/@forms/TextInput";
import { CalendarPicker } from "./CalendarPicker/CalendarPicker";

export function DatePicker({ value, onChange, required, disabled }) {
  const [editValue, setEditValue] = useState(valueToLabel(value));
  const [isOpen, setIsOpen] = useState(false);
  const [isValid, setIsValid] = useState(true);

  const groupRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setEditValue(valueToLabel(value));
  }, [value]);

  return (
    <div className={styles.container} ref={containerRef}>
      <div
        className={classNames(styles.inputGroup, {
          [styles.invalid]: !isValid,
        })}
        ref={groupRef}
      >
        <TextInput
          value={editValue}
          disabled={disabled}
          required={required}
          onChange={(e) => {
            setEditValue(e.target.value);

            const value = parseInputDate(e.target.value);

            setIsValid(value != null);

            if (value) {
              onChange(value);
            }
          }}
        />
        <button
          className={styles.toggleButton}
          disabled={disabled}
          onClick={(e) => {
            log.info("onClick ", e);
            e.preventDefault();
            setIsOpen(!isOpen);
          }}
        >
          <Calendar fontSize={18} strokeWidth={1.5} />
        </button>
      </div>

      <Modal
        isOpen={isOpen}
        title="Choose Date"
        onClose={() => {
          setIsOpen(false);
        }}
      >
        <CalendarPicker
          value={value}
          onChange={(value) => {
            setIsOpen(false);
            setEditValue(valueToLabel(value));
            onChange(value);
          }}
        />
      </Modal>
    </div>
  );
}

DatePicker.propTypes = {
  /**
   * An array of two numbers representing year, month and date. Month runs from 1-12.
   */
  value: PropTypes.arrayOf(PropTypes.number).isRequired,

  /**
   * A callback to fire when the user changes the value. Returns the newly selected time in the same [y, m, d] format.
   */
  onChange: PropTypes.func.isRequired,

  /**
   * Set required=true on the HTML input if true. Helpful for use in forms.
   */
  required: PropTypes.bool,

  /**
   * Use native time picker instead of web-based one on touch devices.
   */
  useNativeOnMobile: PropTypes.bool,
};

function parseInputDate(str) {
  const matches = str.trim().match(/^(\d+)[-./](\d+)[-./](\d+)$/i);

  if (matches) {
    const parts = matches.slice(1, 4);

    const month = parseInt(parts[0]);
    const date = parseInt(parts[1]);
    const year = parseInt(parts[2]);

    const isValid = isDateValid(year, month, date);

    if (isValid) {
      return [year, month, date];
    }
  }

  return null;
}

function isDateValid(year, month, date) {
  if (year < 1900) {
    return false;
  }

  if (month < 1 || month > 12) {
    return false;
  }

  const daysInMonth = getDaysInMonth(new Date(year, month - 1));

  if (date < 1 || date > daysInMonth) {
    return false;
  }

  return true;
}

function dateToValue(date) {
  date = new Date(date);

  const y = date.getFullYear();
  const m = date.getMonth() + 1;
  const d = date.getDate();

  return [y, m, d];
}

function valueToLabel(value) {
  const y = value[0].toString();
  const m = value[1].toString();
  const d = value[2].toString();

  return `${m}.${d}.${y}`;
}

/**
 * Formats value as a string for an HTML date input.
 *
 * @param {[number, number, number]} value
 */
function valueToInputString(value) {
  const y = value[0].toString().padStart(4, "0");
  const m = value[1].toString().padStart(2, "0");
  const d = value[2].toString().padStart(2, "0");

  return `${y}-${m}-${d}`;
}
